import { Download } from "lucide-react";
import { DETTE_BARS, DETTE_STATS, PDF_URL, RACCOURCI } from "./content";
import { useInView } from "./useInView";

const DIGITS = "0123456789".split("");
const Reel = ({ d }) => (
  <span className="odo"><span className="reel" style={{ "--d": d }}>{DIGITS.map((n) => <span key={n} className="block">{n}</span>)}</span></span>
);

const Chart = () => (
  <svg viewBox="0 0 700 262" className="w-full mt-12" role="img" aria-label="Dette publique par habitant de 1980 à 2026">
    {DETTE_BARS.map((b, i) => (
      <rect key={b.year} className={`bar ${b.accent ? "fill-mm-red" : "fill-mm-ink"}`} x={10 + i * 100} y={220 - b.h} width="70" height={b.h} />
    ))}
    <g fontSize="13" textAnchor="middle" fontFamily="ui-monospace,Menlo,monospace" className="fill-mm-ink">
      {DETTE_BARS.map((b, i) => (
        <g key={b.year}>
          <text x={45 + i * 100} y="240">{b.year}</text>
          <text x={45 + i * 100} y="258" fontWeight="700">{b.value}</text>
        </g>
      ))}
    </g>
  </svg>
);

export const Dette = () => {
  const [ref, cls] = useInView(0.12);
  return (
    <section id="dette" ref={ref} className={`bg-mm-paper text-mm-ink py-24 md:py-32 ${cls}`} data-testid="dette-section">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[160px_1fr] gap-12">
        <p className="mm-lab text-mm-golddark">Le dossier · BP France</p>
        <div className="min-w-0">
          <h2 className="disp mm-h2">50 ans de dette · 1975 → 2026</h2>
          <p className="mm-body mt-4 max-w-xl">La facture publique ramenée à chaque habitant. Préalable à « La Grande Question » : qui règle ? combien ? pour quoi ?</p>
          <p className="disp mm-big tab mt-12 text-mm-red" data-testid="dette-par-habitant" aria-label="environ 52 050 euros par habitant">
            ≈<Reel d={5} /><Reel d={2} /> <Reel d={0} /><Reel d={5} /><Reel d={0} /> €
          </p>
          <p className="mm-lab mt-3">par habitant · T2 2026 · dette publique au sens de Maastricht</p>
          <div className="overflow-x-auto"><div className="min-w-[560px]"><Chart /></div></div>
          <div className="st grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 tab">
            {DETTE_STATS.map((s) => (
              <div key={s.big} className={`border-t-4 pt-4 ${s.accent ? "border-mm-red" : "border-mm-ink"}`}>
                <p className="disp text-4xl">{s.big}</p>
                <p className="text-sm mt-2">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 border-l-4 border-mm-red pl-6 max-w-[70ch]">
            <p className="disp text-2xl">Attention au raccourci</p>
            <p className="mm-body mt-2">« ≈ 52 050 € par habitant » ne veut pas dire « 52 050 € à payer demain ». Le ratio sert à poser la question. Il ne remplace pas l'analyse économique.</p>
            <ol className="grid sm:grid-cols-2 gap-6 mt-6">
              {RACCOURCI.map((r, i) => (
                <li key={r.t} className="text-sm"><span className="font-bold">{i + 1}. {r.t}</span><br />{r.d}</li>
              ))}
            </ol>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-8 items-start">
            <div className="bg-mm-alt p-6">
              <p className="mm-lab text-mm-golddark">Aparté · passage à l'euro</p>
              <p className="disp text-3xl mt-3">500 F = 76,22 €</p>
              <p className="text-sm mt-2">1 € = 6,55957 F · 500 € = 3 279,79 F. Le chiffre est resté « 500 », mais 500 n'a plus du tout le même sens. Clin d'œil à Fernand Raynaud.</p>
            </div>
            <div>
              <p className="disp text-2xl">Qui règle ? Combien ? Pour quoi ?</p>
              <p className="mm-body mt-2">On ne peut pas gérer ce que l'on ne mesure pas.</p>
              <p className="mm-lab mt-4 text-mm-golddark leading-6">Recettes · Dépenses · Intérêts · Investissements · Transferts · Capacité de remboursement</p>
            </div>
          </div>
          <a href={PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="dette-pdf-download" className="mt-12 inline-flex items-center gap-3 bg-mm-ink text-mm-paper px-6 py-4 font-bold hover:bg-mm-red transition-colors">
            <Download className="w-4 h-4" /> Télécharger le dossier complet (PDF)
          </a>
          <p className="text-xs mt-4 max-w-xl opacity-70">Chiffres anciens : ordres de grandeur issus de séries historiques, révisables selon les bases de comptes nationaux. Population : estimation INSEE au 1er janvier 2026.</p>
        </div>
      </div>
    </section>
  );
};
